import { FC, useEffect, useState } from 'react';
import {
  TextField,
  Typography,
  Stack,
  Dialog,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  Chip,
  Box,
} from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import ErrorSummary from 'components/common/ErrorSummary';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import {
  Country,
  CreatePersonaCommand,
  InvoicingEligibility,
  ListOptionDTO,
  Persona,
  UpdatePersonaCommand,
} from 'axiosApi/models';
import TypeAhead from 'components/common/TypeAhead';
import Api from 'axiosApi/api';
import { useApi } from 'api/ApiProvider';
import { Button } from '@mui/material';
import ReactIntlWrapper from 'components/i18n/ReactIntlWrapper';
import { z, ZodType } from 'zod';
import { ENTITY_TYPE } from 'common/constants';
import Spinner from 'components/Spinner';
import { Close } from '@mui/icons-material';
import CountrySelect from 'components/common/Selects/CountrySelect';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from '../../common/errorHandler';

type PersonaBillingModalProps = {
  editScopes?: string[];
  deleteScopes?: string[];
  identifier?: string;
  remainingPersonaTypes?: ListOptionDTO[];
  editPersona?: Persona | null;
  entityId?: number;
  idEntityType?: number;
  onClose?: () => void;
  onSave?: () => void;
  billToDisplay?: boolean;
  disabled?: boolean;
  countriesParent?: any[] | null;
};

const personaUpdateSchema = (billToDisplay: boolean): ZodType<UpdatePersonaCommand> =>
  z.object({
    id: z.number().min(1, intl.get('validate.errors.isRequired')),
    name: z.string().min(1, intl.get('validate.errors.isRequired')),
    email: z
      .string()
      .min(1, intl.get('validate.errors.isRequired'))
      .email(intl.get('validate.errors.invalidEmail')),
    taxID: z.string().min(1, intl.get('validate.errors.isRequired')),
    idCountry: z.number().min(1, intl.get('validate.errors.isRequired')),
    personaToIds: billToDisplay
      ? z.array(z.number()).min(1, intl.get('validate.errors.isRequired'))
      : z.array(z.number()).optional(),
  });

const personaCreateSchema = (billToDisplay: boolean): ZodType<CreatePersonaCommand> =>
  z.object({
    name: z.string().min(1, intl.get('validate.errors.isRequired')),
    email: z
      .string()
      .min(1, intl.get('validate.errors.isRequired'))
      .email(intl.get('validate.errors.invalidEmail')),
    taxID: z.string().min(1, intl.get('validate.errors.isRequired')),
    idCountry: z.number().min(1, intl.get('validate.errors.isRequired')),
    idPersonaType: z.number().min(1, intl.get('validate.errors.isRequired')),
    idEntity: z.number().optional(),
    idEntityType: z.number().optional(),
    personaToIds: billToDisplay
      ? z.array(z.number()).min(1, intl.get('validate.errors.isRequired'))
      : z.array(z.number()).optional(),
  });

const PersonaBillingModal: FC<PersonaBillingModalProps> = ({
  editScopes = [],
  deleteScopes = [],
  identifier,
  remainingPersonaTypes = [],
  editPersona = null,
  entityId = 0,
  idEntityType = 0,
  onClose = () => {},
  onSave = () => {},
  billToDisplay = true,
  disabled = false,
  countriesParent,
}) => {
  const api: Api = useApi();
  const toast = useToastMessageQueue();
  const [mode, setMode] = useState<'edit' | 'add'>('add');
  const { invoiceApi, addressApi }: Api = useApi();
  const [errors, setErrors] = useState<Record<string, string | string[]>>({});
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState(null);

  // Form state
  const [personaTo, setPersonaTo] = useState<ListOptionDTO[]>([]);
  const [formData, setFormData] = useState<any>({
    id: 0,
    name: '',
    taxID: '',
    idCountry: 0,
    personaToIds: [],
  });

  const [personaTypes, setPersonaTypes] = useState<ListOptionDTO[]>([]);
  const [country, setCountry] = useState<Country>();

  // Handle form input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle corporate entities selection
  const handleCorporateEntitiesChange = (event: any, newValues: ListOptionDTO[]) => {
    setPersonaTo(newValues);
  };

  // Handle persona type selection
  const handlePersonaTypeChange = (e: any) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, idPersonaType: value }));
  };

  const fetchCountries = async () => {
    if (countriesParent && countriesParent.length > 0) {
      setCountries(countriesParent);
      return;
    }
    if (typeof countriesParent === 'undefined') {
      const response = await api.addressApi
        .apiVversionAddressCountryAllGet('1', {})
        .then((response) => {
          if (response.data) {
            const countriesData = response.data.data;
            if (Array.isArray(countriesData) && countriesData.length > 0) {
              setCountries(countriesData);
            } else {
              setErrors({
                summary: ['No countries in the database, will not be able to save form'],
              });
            }
          }
        })
        .catch((error) => {
          handleAPIError(error, toast, errors);
          setErrors({ ...errors, summary: ['Failed to fetch countries'] });
        });
    }
  };

  useEffect(() => {
    fetchCountries();
  }, [formData.idCountry, countriesParent]);

  const handleCountryChange = (e) => {
    if (!e) {
      setFormData({ ...formData, idCountry: null, country: { name: '', labelKey: '' } });
      setCountry({ id: null, labelKey: '' });
      setErrors({});
      return;
    }
    const inputVal = {
      ...formData,
      idCountry: e.id,
      country: { ...e.country, name: intl.get(e?.labelKey), labelKey: e?.labelKey },
      idStateProvince: null,
      stateProvince: { name: '' },
    };
    setFormData({ ...inputVal });
    setCountry({ id: e.id, labelKey: e?.labelKey });
    setErrors({});
  };

  const updatePersona = async (input: UpdatePersonaCommand) => {
    try {
      setLoading(true);
      await invoiceApi.apiVversionPersonaPut('1', input, {});
      toast.success({ message: intl.get('persona.toast.success.update') });
    } catch (error) {
      console.error('Error updating persona:', error);
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  const savePersona = async (input: CreatePersonaCommand) => {
    try {
      setLoading(true);
      await invoiceApi.apiVversionPersonaPost('1', input, {});
      toast.success({ message: intl.get('persona.toast.success.create') });
    } catch (error) {
      console.error('Error creating persona:', error);
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setErrors({});
      const personaToIds = personaTo.map((entity) => entity.id);

      // Validate and submit the form data
      if (mode === 'edit') {
        const parsedData = await personaUpdateSchema(billToDisplay).parseAsync({
          ...formData,
          id: editPersona?.id,
          personaToIds: personaToIds,
          idCountry: formData.idCountry,
        });
        await updatePersona(parsedData);
      } else {
        const parsedData = await personaCreateSchema(billToDisplay).parseAsync({
          ...formData,
          idEntity: entityId,
          idEntityType: idEntityType,
          personaToIds: personaToIds,
          idCountry: formData.idCountry,
        });
        await savePersona(parsedData);
      }

      // Close modal and trigger parent callback on success
      closeModal();
      onSave && onSave();
    } catch (error) {
      // Handle validation errors
      if (error instanceof z.ZodError) {
        const errorMap: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path) {
            errorMap[err.path[0].toString()] = err.message;
          }
        });
        setErrors(errorMap);
      } else {
        console.error('Unexpected error during form submission:', error);
      }
    }
  };

  useEffect(() => {
    if (editPersona) {
      setMode('edit');
      setOpen(true);
      setFormData({
        id: editPersona.id,
        name: editPersona.name,
        email: editPersona.email,
        taxID: editPersona.taxID,
        idPersonaType: editPersona.idPersonaType,
        idCountry: editPersona.idCountry,
        country: { 
          name: intl.get(editPersona.country?.labelKey), 
          labelKey: editPersona.country?.labelKey 
        },
        personaToIds: editPersona.invoicingEligibilities.map(
          (entity: InvoicingEligibility) => entity.idPersonaTo
        ),
      });
      setPersonaTo(
        editPersona.invoicingEligibilities.map((entity: InvoicingEligibility) => ({
          id: entity.idPersonaTo,
          label: entity.personaTo.name,
        }))
      );
      setPersonaTypes(
        remainingPersonaTypes.concat([
          { id: editPersona.idPersonaType, label: editPersona.personaType?.labelKey },
        ])
      );
      setCountry({ id: editPersona.idCountry, labelKey: editPersona.country?.labelKey });
      setErrors({});
    }
  }, [editPersona, open]);

  const newBillingPersonaClick = () => {
    setMode('add');
    setOpen(true);
    // Reset form when opening in add mode
    setFormData({
      id: 0,
      name: '',
      email: '',
      taxID: '',
      idCountry: null,
      country: { name: '', labelKey: '' },
      personaToIds: [],
      idPersonaType: '',
    });
    if (remainingPersonaTypes?.length === 1) {
      setFormData((prev) => ({ ...prev, idPersonaType: remainingPersonaTypes[0].id }));
    }

    if (remainingPersonaTypes?.length === 0) {
      setFormData((prev) => ({ ...prev, idPersonaType: editPersona.idPersonaType }));
    }
    setPersonaTypes(remainingPersonaTypes);
    setPersonaTo([]);
    setCountry(undefined);
    setErrors({});
  };

  const closeModal = () => {
    setOpen(false);
    onClose && onClose();
  };

  return (
    <>
      {loading && <Spinner small={false} key={'loadingSpinner'} />}
      <PermissionsGate
        identifier={identifier}
        viewScopes={identifier ? undefined : editScopes}
        editScopes={identifier ? undefined : editScopes}
        viewRoles={[]}
        editRoles={[]}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={disabled || remainingPersonaTypes.length === 0}
            onClick={newBillingPersonaClick}
          >
            {intl.get('personaCompact.newPersona.button')}
          </Button>
        </Box>
      </PermissionsGate>
      <Dialog
        open={open}
        fullWidth={true}
        maxWidth="sm"
        onClose={(event: any, reason: string) => {
          if (reason !== 'backdropClick') {
            setOpen(false);
          }
        }}
        PaperProps={{ sx: { overflow: 'unset' } }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography color="primary" variant="h2">
            {editPersona && open ? intl.get('persona.title.edit') : intl.get('persona.title.add')}
          </Typography>
          <IconButton aria-label="close" onClick={closeModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'unset' }}>
          <Stack spacing={2}>
            <ErrorSummary errors={errors} showEverything={true} mode="warning"></ErrorSummary>
            <TextField
              fullWidth
              id="name"
              name="name"
              label={`${intl.get('personaCompact.table.thead.name')}*`}
              error={!!errors.name}
              margin="dense"
              value={formData.name}
              onChange={handleChange}
              disabled={disabled || loading}
            />
            <TextField
              name="email"
              label={`${intl.get('employeeList.table.thead.email')}*`}
              fullWidth
              margin="dense"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              disabled={disabled || loading}
            />
            <FormControl size="small" error={!!errors.idPersonaType}>
              <InputLabel id="idPersonaTypeLabel">{`${intl.get('personaCompact.table.thead.type')}*`}</InputLabel>
              <Select
                fullWidth
                labelId="idPersonaTypeLabel"
                label={`${intl.get('personaCompact.table.thead.type')}*`}
                id="idPersonaType"
                name="idPersonaType"
                error={!!errors.idPersonaType}
                margin="dense"
                value={formData.idPersonaType}
                onChange={handlePersonaTypeChange}
                disabled={disabled || loading || remainingPersonaTypes.length === 0}
              >
                {personaTypes.map((option: ListOptionDTO) => (
                  <MenuItem key={option.id} value={option.id}>
                    {intl.get(option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              id="taxID"
              name="taxID"
              label={`${intl.get('personaCompact.table.thead.taxId')}*`}
              error={!!errors.taxID}
              margin="dense"
              value={formData.taxID}
              onChange={handleChange}
              disabled={disabled || loading}
            />
            <CountrySelect
              handleCountryChange={handleCountryChange}
              selectedCountry={formData?.country?.labelKey}
              countries={countries}
              inputCountry={formData?.country?.name}
              disabled={disabled || loading}
            />
            {billToDisplay && (
              <>
                <Typography>{intl.get('personaForm.label.SelectBillTo')}*</Typography>
                <TypeAhead
                  apiPromise={(searchTerm: string) =>
                    invoiceApi
                      .apiVversionListPersonaListGet('1', ENTITY_TYPE.CORPORATE_ENTITY)
                      .then((response) => {
                        return {
                          data: {
                            data: response?.data?.data?.queryResult.filter(
                              (entity: ListOptionDTO) =>
                                entity.label.toLowerCase().includes(searchTerm.toLowerCase())
                            ),
                          },
                        };
                      })
                  }
                  getOptionLabel={(option: ListOptionDTO | any) => option.label}
                  value={personaTo}
                  disableClearable={true}
                  multiple={true}
                  label={intl.get('corporateEntitySelect.placeholder.corporateEntity')}
                  onChange={handleCorporateEntitiesChange}
                  error={!!errors.personaToIds}
                  disabled={disabled || loading}
                  autocompleteProps={{
                    renderTags(value, getTagProps, ownerState) {
                      return value.map((option, index) => (
                        <Chip
                          key={option.id}
                          label={option.label}
                          color="primary"
                          variant="outlined"
                          {...getTagProps({ index })}
                        />
                      ));
                    },
                  }}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {loading ? (
            <Spinner small={true} />
          ) : (
            <>
              <Button
                variant="outlined"
                color="inherit"
                onClick={closeModal}
                className="btn btn-outline-dark"
              >
                {intl.get('cancel.button')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                className="btn btn-primary"
                disabled={loading}
              >
                <SaveIcon className="mb-1" /> {intl.get('save.button')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PersonaBillingModal;